import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  selectAssetNode,
  toggleTreeNode,
  setAssetSearchTerm,
  expandAllNodes,
  collapseAllNodes,
  selectSidebarItem,
  deselectSidebarItem,
  deselectTemplate,
} from '../actions';
import { buildGroupNode, buildItemNode, findNodeById, getBreadcrumbPath } from '../helpers';

/**
 * What is currently selected in the tree, and the handlers that change it.
 *
 * A sidebar row and a tree node are two ways of selecting the same thing, so
 * both resolve to one `selectedNode` and one `breadcrumb` for the detail pane.
 */
export function useAssetSelection({
  tree,
  selectedNodeId,
  selectedSidebarItem,
  groups,
  items,
}: {
  tree: any;
  selectedNodeId: string;
  selectedSidebarItem: any;
  groups: any[];
  items: any[];
}) {
  const dispatch = useDispatch();

  const componentTree = useMemo(() => {
    if (!selectedSidebarItem) return tree;
    const { itemType, data } = selectedSidebarItem;
    return itemType === 'group' ? buildGroupNode(data, 0) : null;
  }, [tree, selectedSidebarItem]);

  const selectedNode = useMemo(() => {
    if (selectedSidebarItem) {
      const { itemType, data } = selectedSidebarItem;
      if (itemType !== 'group') return buildItemNode(data);
      const root = componentTree;
      if (!root) return null;
      if (selectedNodeId && selectedNodeId !== root.id) {
        const inner = findNodeById(root, selectedNodeId);
        if (inner) return inner;
      }
      return root;
    }
    return findNodeById(tree, selectedNodeId);
  }, [tree, componentTree, selectedNodeId, selectedSidebarItem]);

  const breadcrumb = useMemo(() => {
    if (selectedSidebarItem) {
      const { itemType, data } = selectedSidebarItem;
      if (itemType === 'group' && componentTree) {
        const path = getBreadcrumbPath(componentTree, selectedNodeId);
        if (path?.length) return path;
      }
      return [
        {
          id: `${itemType}-${data.id}`,
          name: data.name || `${itemType === 'group' ? 'Group' : 'Item'} #${data.id}`,
          type: itemType === 'group' ? 'group' : 'item',
        },
      ];
    }
    return getBreadcrumbPath(tree, selectedNodeId) || [];
  }, [tree, componentTree, selectedNodeId, selectedSidebarItem]);

  const handleBackToTemplates = () => {
    dispatch(deselectTemplate());
    dispatch(deselectSidebarItem());
  };

  const handleSelectGroup = useCallback(
    (group: any) => {
      dispatch(deselectTemplate());
      dispatch(selectSidebarItem('group', group.id, group));
    },
    [dispatch]
  );

  const handleSelectItem = useCallback(
    (item: any) => {
      dispatch(deselectTemplate());
      dispatch(selectSidebarItem('item', item.id, item));
    },
    [dispatch]
  );

  // Source chips carry only an id, so the row is re-read from the store to get
  // the current record rather than whatever was embedded when it rendered.
  const handleSelectSource = useCallback(
    (source: { id: number; kind: 'group' | 'item' } | null | undefined) => {
      if (!source?.id) return;
      if (source.kind === 'group') {
        const fresh = (groups || []).find((g: any) => g.id === source.id);
        if (fresh) handleSelectGroup(fresh);
      } else if (source.kind === 'item') {
        const fresh = (items || []).find((i: any) => i.id === source.id);
        if (fresh) handleSelectItem(fresh);
      }
    },
    [groups, items, handleSelectGroup, handleSelectItem]
  );

  const handleSelectNode = (nodeId: string) => dispatch(selectAssetNode(nodeId));
  const handleToggleNode = (nodeId: string) => dispatch(toggleTreeNode(nodeId));
  const handleSearchChange = (term: string) => dispatch(setAssetSearchTerm(term));
  const handleExpandAll = () => dispatch(expandAllNodes());
  const handleCollapseAll = () => dispatch(collapseAllNodes());

  return {
    componentTree,
    selectedNode,
    breadcrumb,
    handleBackToTemplates,
    handleSelectGroup,
    handleSelectItem,
    handleSelectSource,
    handleSelectNode,
    handleToggleNode,
    handleSearchChange,
    handleExpandAll,
    handleCollapseAll,
  };
}
